import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaFileInvoice,
  FaPencilAlt,
  FaStickyNote,
  FaUserTie,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaUndo,
} from "react-icons/fa";
import { FaBuildingUser } from "react-icons/fa6";
import { useUpdateFirmInfoMutation, useUpdateFirmDealerRelationMutation } from "../../../../store";
import {
  useGetFirmBillingSummaryQuery,
  useGetFirmConsumptionSummaryQuery,
} from "../../../../store/storageRoles/firmApi";
import {
  useGetDealerRolesFirmBillingSummaryQuery,
  useGetDealerRolesFirmConsumptionSummaryQuery,
} from "../../../../store/dealerRoles/firmApi";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useAlert } from "../../../../context/AlertContext";
import FirmReportSettingsBox from "../FirmReportSettingsBox";
import FirmBillingSummaryCard from "./FirmBillingSummaryCard";
import FirmConsumptionSummaryCard from "./FirmConsumptionSummaryCard";
import FirmContactsField from "../../FirmContactsField";
import ParentFirmPicker from "../../ParentFirmPicker";
import { parentOfFirm, extraDealersOf, parentSavePlan, parentKindLabel } from "../../../../utils/firmParent";
import { toContactRows, cleanContacts, validateContacts } from "../../../../utils/firmContacts";

// ─── Build form state from firm ───
const buildFormState = (firm) => ({
  firmName: firm?.firmName || "",
  bayserNo: firm?.bayserNo || "",
  // Firma sahibi — ad soyad, opsiyonel (#243)
  ownerName: firm?.ownerName || "",
  // Muhataplar — ad + telefon, birden fazla (#367)
  contacts: toContactRows(firm?.contacts),
  tel: firm?.tel || "",
  email: firm?.email || "",
  city: firm?.address?.city || "",
  town: firm?.address?.town || "",
  addressText: firm?.address?.text || "",
  isActive: firm?.isActive !== false,
  isCorporate: firm?.isCorporate || false,
  note: firm?.note || "",
  officialNumber: firm?.officialNumber || "",
  taxNumber: firm?.taxNumber || "",
  taxOffice: firm?.taxOffice || "",
  // Tek üst firma: ana firma ya da bayi (#373)
  parent: parentOfFirm(firm),
});

// ─── Editable text field (local state, no API) ───
const EditableField = ({ label, value, icon: Icon, onChange, type = "text", placeholder = "-", isDirty, multiline = false }) => {
  const [editing, setEditing] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) setEditing(false);
    if (e.key === "Escape") setEditing(false);
  };

  return (
    <div className={`group rounded-xl border p-3 sm:p-4 transition-all hover:border-blue-200 hover:bg-blue-50/30 ${isDirty ? "border-blue-300 bg-blue-50/40" : "border-gray-200 bg-gray-50"}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />}
          {label}
          {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-1" />}
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-all rounded-md hover:bg-blue-50" title="Düzenle">
            <FaPencilAlt className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        )}
      </div>
      {editing ? (
        multiline ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setEditing(false)}
            className="w-full text-sm text-gray-900 bg-white border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none min-h-[60px]"
            autoFocus
            placeholder={placeholder}
            rows={2}
          />
        ) : (
          <input
            type={type}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setEditing(false)}
            className="w-full text-sm text-gray-900 bg-white border border-blue-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            autoFocus
            placeholder={placeholder}
          />
        )
      ) : (
        <p
          className="text-sm font-semibold text-gray-800 leading-relaxed cursor-pointer hover:text-blue-700 transition-colors"
          onClick={() => setEditing(true)}
        >
          {value || <span className="text-gray-400 italic">{placeholder}</span>}
        </p>
      )}
    </div>
  );
};

// ─── Read-only info field ───
const InfoField = ({ label, value, icon: Icon, action, muted = false, hint }) => (
  <div className={`rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4 ${muted ? "opacity-50" : ""}`} aria-disabled={muted || undefined}>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide">
        {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />}
        {label}
      </div>
      {action}
    </div>
    <p className="text-sm font-semibold text-gray-800 leading-relaxed">
      {value || <span className="text-gray-400 italic">-</span>}
    </p>
    {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
  </div>
);

// ─── Toggle field (local state, no API) ───
const ToggleField = ({ label, value, icon: Icon, onToggle, trueLabel = "Evet", falseLabel = "Hayır", isDirty }) => (
  <div className={`group rounded-xl border p-3 sm:p-4 transition-all hover:border-blue-200 ${isDirty ? "border-blue-300 bg-blue-50/40" : "border-gray-200 bg-gray-50"}`}>
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
          {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />}
          {label}
          {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-1" />}
        </div>
        <span className={`text-sm font-semibold ${value ? "text-emerald-700" : "text-gray-500"}`}>
          {value ? trueLabel : falseLabel}
        </span>
      </div>
      <button onClick={() => onToggle(!value)} className="p-1 transition-colors" title={value ? "Devre dışı bırak" : "Etkinleştir"}>
        {value ? (
          <FaToggleOn className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500 hover:text-emerald-600 transition-colors" />
        ) : (
          <FaToggleOff className="w-7 h-7 sm:w-8 sm:h-8 text-gray-300 hover:text-gray-400 transition-colors" />
        )}
      </button>
    </div>
  </div>
);

const InfoTab = ({
  firm,
  onFirmUpdate,
  onSelectedFirm,
  readOnly = false,
  onOpenReportSettings,
}) => {
  const { auth } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermission();
  const { showError } = useAlert();
  // Sunucu hatası kullanıcıya gösterilir (#194). Önceden üç işlem de hatayı
  // "üst bileşen gösterir" diye yutuyordu ama gösteren yoktu — örn. şubeye
  // bayi eklemek 400 dönüyor, ekranda hiçbir şey olmuyordu.
  const reportError = useCallback(
    (e, fallback) => showError(e?.data?.message || fallback),
    [showError]
  );
  const [updateFirm] = useUpdateFirmInfoMutation();
  const [updateDealerRelation] = useUpdateFirmDealerRelationMutation();

  const isDealer = auth?.role === "dealer";
  const isDealerOwnFirmFromRelation = isDealer && (firm?.dealerRelation === "anaBayi" || firm?.dealerRelation === "bayi");
  const hasAnyEditPerm = hasAnyPermission("firms.editName", "firms.editBayserNo", "firms.editDetails", "firms.editActivity", "firms.editMainFirm", "firms.editDealerRelation");
  const readOnlyMode = readOnly || isDealerOwnFirmFromRelation || !hasAnyEditPerm;

  const isCustomer = firm?.firmType === "customer";

  // Özet kartları (Son Fatura + Kota Tüketimi) her biri AYRI izne bağlı; izin
  // yoksa kart tamamen gizlenir. Rol-farkındalıklı: depo → firms.*, bayi →
  // firma tipine göre customers.* / dealers.*.
  const billingSummaryPerm = isDealer
    ? isCustomer
      ? "customers.billingSummary"
      : "dealers.billingSummary"
    : "firms.billingSummary";
  const consumptionSummaryPerm = isDealer
    ? isCustomer
      ? "customers.consumptionSummary"
      : "dealers.consumptionSummary"
    : "firms.consumptionSummary";
  const canViewBillingSummary = hasPermission(billingSummaryPerm);
  const canViewConsumptionSummary = hasPermission(consumptionSummaryPerm);

  // Otomatik rapor ayarları kutusu — izne bağlı (kullanıcı isteği 2026-08-14).
  // Yetki zaten kataloglarda vardı, arayüzde kontrol edilmiyordu: yetkisiz
  // kullanıcı kutuyu görüp çekmeceyi açabiliyordu.
  const autoReportPerm = isDealer
    ? isCustomer
      ? "customers.editAutoReport"
      : "dealers.editAutoReport"
    : "firms.editAutoReport";
  const canEditAutoReport = hasPermission(autoReportPerm);

  const [form, setForm] = useState(() => buildFormState(firm));
  const [saving, setSaving] = useState(false);

  // Sync form state when firm prop changes (from parent/API)
  useEffect(() => {
    if (firm) setForm(buildFormState(firm));
  }, [firm]);

  // Check if form has changes compared to firm
  const originalForm = useMemo(() => buildFormState(firm), [firm]);
  const hasChanges = useMemo(() => {
    if (!firm) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm, firm]);

  // Which fields are dirty
  const isDirty = useCallback((field) => {
    if (!firm) return false;
    if (field === "contacts" || field === "parent") return JSON.stringify(form[field]) !== JSON.stringify(originalForm[field]);
    return form[field] !== originalForm[field];
  }, [form, originalForm, firm]);

  const contactErrors = useMemo(() => validateContacts(form.contacts), [form.contacts]);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleReset = () => setForm(buildFormState(firm));

  const handleSave = useCallback(async () => {
    if (!firm || !hasChanges) return;
    if (Object.keys(contactErrors).length) {
      showError("Muhatap bilgilerini kontrol edin.");
      return;
    }
    setSaving(true);
    // Üst firma (#373): sunucu kilidi yüzünden önce eski bağlantı kalkar,
    // bayi en son yazılır (utils/firmParent.parentSavePlan)
    const plan = parentSavePlan(firm, form.parent);
    try {
      if (plan.clearDealersFirst) {
        await updateDealerRelation({ id: firm._id, dealerFirmIds: [], token: auth.accessToken }).unwrap();
      }
      const updateData = {
        firmName: form.firmName,
        bayserNo: form.bayserNo,
        mainFirmName: plan.mainFirmName,
        isCorporate: form.isCorporate,
        officialNumber: form.officialNumber || "11111111111",
        taxNumber: form.taxNumber || "11111111111",
        taxOffice: form.taxOffice || "11111111111",
        email: form.email,
        tel: form.tel,
        ownerName: form.ownerName.trim(),
        contacts: cleanContacts(form.contacts),
        address: { city: form.city, town: form.town, text: form.addressText },
        isActive: form.isActive,
        note: form.note,
        token: auth.accessToken,
      };
      let result = await updateFirm({ ...updateData, id: firm._id }).unwrap();
      if (plan.dealerFirmIds) {
        result = await updateDealerRelation({
          id: firm._id,
          dealerFirmIds: plan.dealerFirmIds,
          token: auth.accessToken,
        }).unwrap();
      }
      if (result?.data?.firm) onFirmUpdate(result.data.firm);
    } catch (e) {
      reportError(e, "Firma bilgileri kaydedilemedi.");
    }
    setSaving(false);
  }, [firm, form, hasChanges, contactErrors, auth.accessToken, updateFirm, updateDealerRelation, onFirmUpdate, reportError, showError]);

  if (!firm) return null;

  // TEK ÜST FİRMA (#373): ana firma YA DA bayi. Sunucu ikisini birlikte
  // reddeder (#197 kilidi); panel tek alanda gösterir, kaydederken önce eski
  // bağlantıyı kaldırır (parentSavePlan).
  const savedParent = parentOfFirm(firm);
  const extraDealers = extraDealersOf(firm);
  const canMain = hasPermission("firms.editMainFirm");
  const canDealer = isCustomer && hasPermission("firms.editDealerRelation");
  // Mevcut bağlantıyı değiştirme yetkisi de olmalı (bayiden ana firmaya
  // geçmek bayiyi kaldırmayı gerektirir)
  const canEditParent =
    !readOnlyMode &&
    (canMain || canDealer) &&
    (savedParent?.kind !== "dealer" || canDealer) &&
    (savedParent?.kind !== "main" || canMain);
  const parentKindChanged =
    !!savedParent && !!form.parent && savedParent.kind !== form.parent.kind;
  const firmIsDealer = firm.firmType === "dealer";
  const firmTypeLabel = !form.parent
    ? firmIsDealer ? "Ana Bayi" : "Ana Firma"
    : form.parent.kind === "dealer"
      ? "Bayi Müşterisi"
      : firmIsDealer ? "Alt Bayi" : "Alt Firma";
  // Bireysel firmada vergi kutuları görünür ama pasif (#373); değerler
  // sunucunun yer tutucusu olduğundan gösterilmez
  const taxMuted = !form.isCorporate;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="space-y-4 p-3 sm:p-4">
          {/* ROW 0 — Özet Kutuları (her biri ayrı izne bağlı; izin yoksa gizli) */}
          {(canViewBillingSummary || canViewConsumptionSummary) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {canViewBillingSummary && (
                <FirmBillingSummaryCard
                  firmId={firm._id}
                  token={auth.accessToken}
                  useSummaryQuery={
                    isDealer
                      ? useGetDealerRolesFirmBillingSummaryQuery
                      : useGetFirmBillingSummaryQuery
                  }
                />
              )}
              {canViewConsumptionSummary && (
                <FirmConsumptionSummaryCard
                  firmId={firm._id}
                  token={auth.accessToken}
                  useSummaryQuery={
                    isDealer
                      ? useGetDealerRolesFirmConsumptionSummaryQuery
                      : useGetFirmConsumptionSummaryQuery
                  }
                />
              )}
            </div>
          )}

          {/* ROW 1 — İletişim + Firma Yapısı */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* İletişim Bilgileri */}
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">İletişim Bilgileri</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {hasPermission("firms.editName") && !readOnlyMode ? (
                  <EditableField
                    label="Firma Adı"
                    value={form.firmName}
                    icon={FaBuilding}
                    onChange={(val) => updateForm("firmName", val)}
                    isDirty={isDirty("firmName")}
                    placeholder="Firma adı"
                  />
                ) : (
                  <InfoField label="Firma Adı" value={form.firmName} icon={FaBuilding} />
                )}
                {hasPermission("firms.editBayserNo") && !readOnlyMode ? (
                  <EditableField
                    label="Bayser No"
                    value={form.bayserNo}
                    icon={FaFileInvoice}
                    onChange={(val) => updateForm("bayserNo", val)}
                    isDirty={isDirty("bayserNo")}
                    placeholder="Bayser numarası"
                  />
                ) : (
                  <InfoField label="Bayser No" value={form.bayserNo} icon={FaFileInvoice} />
                )}
                {hasPermission("firms.editDetails") && !readOnlyMode ? (
                  <>
                    <EditableField
                      label="Firma Sahibi"
                      value={form.ownerName}
                      icon={FaUserTie}
                      onChange={(val) => updateForm("ownerName", val.slice(0, 100))}
                      isDirty={isDirty("ownerName")}
                      placeholder="Ad Soyad"
                    />
                    <EditableField
                      label="Telefon"
                      value={form.tel}
                      icon={FaPhone}
                      type="tel"
                      onChange={(val) => updateForm("tel", val)}
                      isDirty={isDirty("tel")}
                      placeholder="Telefon"
                    />
                    <EditableField
                      label="E-posta"
                      value={form.email}
                      icon={FaEnvelope}
                      type="email"
                      onChange={(val) => updateForm("email", val)}
                      isDirty={isDirty("email")}
                      placeholder="E-posta"
                    />
                    <EditableField
                      label="Şehir"
                      value={form.city}
                      icon={FaMapMarkerAlt}
                      onChange={(val) => updateForm("city", val)}
                      isDirty={isDirty("city")}
                      placeholder="Şehir"
                    />
                    <EditableField
                      label="İlçe"
                      value={form.town}
                      icon={FaMapMarkerAlt}
                      onChange={(val) => updateForm("town", val)}
                      isDirty={isDirty("town")}
                      placeholder="İlçe"
                    />
                    <EditableField
                      label="Adres"
                      value={form.addressText}
                      icon={FaMapMarkerAlt}
                      onChange={(val) => updateForm("addressText", val)}
                      isDirty={isDirty("addressText")}
                      placeholder="Adres detayı"
                    />
                  </>
                ) : (
                  <>
                    <InfoField label="Firma Sahibi" value={form.ownerName} icon={FaUserTie} />
                    <InfoField label="Telefon" value={form.tel} icon={FaPhone} />
                    <InfoField label="E-posta" value={form.email} icon={FaEnvelope} />
                    <InfoField label="Şehir" value={form.city} icon={FaMapMarkerAlt} />
                    <InfoField label="İlçe" value={form.town} icon={FaMapMarkerAlt} />
                    <InfoField label="Adres" value={form.addressText} icon={FaMapMarkerAlt} />
                  </>
                )}
                {/* Muhataplar (#367) — Firma Sahibi ve telefona ek, tam genişlik */}
                <div className="lg:col-span-2">
                  <FirmContactsField
                    compact
                    value={form.contacts}
                    onChange={(rows) => updateForm("contacts", rows)}
                    errors={contactErrors}
                    readOnly={!hasPermission("firms.editDetails") || readOnlyMode}
                    isDirty={isDirty("contacts")}
                  />
                </div>
              </div>
            </section>

            {/* Firma Yapısı */}
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
              <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Firma Yapısı</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <InfoField
                  label="Firma Türü"
                  value={firmTypeLabel}
                  icon={FaBuildingUser}
                />
                {/* Üst Firma — ana firma ya da bayi (#373) */}
                <div className={`rounded-xl border p-3 sm:p-4 transition-all ${isDirty("parent") ? "border-blue-300 bg-blue-50/40" : "border-gray-200 bg-gray-50"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-600 tracking-wide">
                      <FaBuilding className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />
                      ÜST FİRMA
                      {isDirty("parent") && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-1" />}
                    </div>
                    {savedParent && !isDirty("parent") && (
                      <button
                        onClick={() =>
                          onSelectedFirm(savedParent.kind === "main" ? firm.mainFirm : (firm.dealerFirm || [])[0])
                        }
                        className="text-[11px] text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors"
                      >
                        Görüntüle →
                      </button>
                    )}
                  </div>
                  {canEditParent ? (
                    <ParentFirmPicker
                      compact
                      firmType={firm.firmType}
                      excludeFirmId={firm._id}
                      value={form.parent}
                      onChange={(parent) => updateForm("parent", parent)}
                      allowMain={canMain}
                      allowDealer={canDealer}
                    />
                  ) : (
                    <p className="text-sm font-semibold text-gray-800 leading-relaxed">
                      {savedParent ? (
                        <>
                          {savedParent.firmName}
                          <span className="ml-1.5 text-[11px] font-medium text-gray-500">
                            ({parentKindLabel(savedParent, firm.firmType)})
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </p>
                  )}
                  {extraDealers.length > 0 && (
                    <p className="mt-1.5 text-[11px] text-gray-500">
                      Diğer bağlı bayiler: {extraDealers.map((d) => d?.firmName || "Bayi").join(", ")}. Üst firma
                      değiştirilince tek bayi kalır.
                    </p>
                  )}
                  {parentKindChanged && (
                    <p className="mt-1.5 text-[11px] text-amber-700">
                      Kaydedince {savedParent.kind === "dealer" ? "bayi bağlantısı" : "ana firma"} kaldırılacak.
                    </p>
                  )}
                </div>
                {/* Aktiflik */}
                {hasPermission("firms.editActivity") && !readOnlyMode ? (
                  <ToggleField
                    label="Aktiflik"
                    value={form.isActive}
                    icon={FaUserTie}
                    onToggle={(val) => updateForm("isActive", val)}
                    isDirty={isDirty("isActive")}
                    trueLabel="Aktif"
                    falseLabel="Pasif"
                  />
                ) : (
                  <InfoField label="Aktiflik" value={form.isActive ? "Aktif" : "Pasif"} icon={FaUserTie} />
                )}
                {/* Kurumsal/Bireysel */}
                {hasPermission("firms.editDetails") && !readOnlyMode ? (
                  <ToggleField
                    label="Kurumsal"
                    value={form.isCorporate}
                    icon={FaBuilding}
                    onToggle={(val) => updateForm("isCorporate", val)}
                    isDirty={isDirty("isCorporate")}
                    trueLabel="Kurumsal"
                    falseLabel="Bireysel"
                  />
                ) : (
                  <InfoField label="Kurumsal" value={form.isCorporate ? "Kurumsal" : "Bireysel"} icon={FaBuilding} />
                )}
                {/* Vergi Bilgileri — bireyselde görünür ama pasif (#373) */}
                {taxMuted ? (
                  <>
                    <InfoField muted label="TC No / Vergi No" value="-" icon={FaFileInvoice} hint="Bireysel firmada kullanılmaz" />
                    <InfoField muted label="Vergi Numarası" value="-" icon={FaFileInvoice} />
                    <InfoField muted label="Vergi Dairesi" value="-" icon={FaFileInvoice} />
                  </>
                ) : (
                  <>
                    {readOnlyMode || !hasPermission("firms.editDetails") ? (
                      <>
                        <InfoField label="TC No / Vergi No" value={form.officialNumber || "-"} icon={FaFileInvoice} />
                        <InfoField label="Vergi Numarası" value={form.taxNumber || "-"} icon={FaFileInvoice} />
                        <InfoField label="Vergi Dairesi" value={form.taxOffice || "-"} icon={FaFileInvoice} />
                      </>
                    ) : (
                      <>
                        <EditableField
                          label="TC No / Vergi No"
                          value={form.officialNumber}
                          icon={FaFileInvoice}
                          onChange={(val) => updateForm("officialNumber", val)}
                          isDirty={isDirty("officialNumber")}
                          placeholder="TC/Vergi No"
                        />
                        <EditableField
                          label="Vergi Numarası"
                          value={form.taxNumber}
                          icon={FaFileInvoice}
                          onChange={(val) => updateForm("taxNumber", val)}
                          isDirty={isDirty("taxNumber")}
                          placeholder="Vergi numarası"
                        />
                        <EditableField
                          label="Vergi Dairesi"
                          value={form.taxOffice}
                          icon={FaFileInvoice}
                          onChange={(val) => updateForm("taxOffice", val)}
                          isDirty={isDirty("taxOffice")}
                          placeholder="Vergi dairesi"
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </section>
          </div>

          {/* ROW 2 — Rapor ayarları + Notlar yan yana (#426); rapor ayarı
              kutusu yoksa Notlar tam genişlik. Bayi bağlantısı Üst Firma
              alanında (#373). */}
          <div
            className={
              canEditAutoReport && typeof onOpenReportSettings === "function"
                ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                : ""
            }
          >
          {canEditAutoReport && typeof onOpenReportSettings === "function" && (
            <FirmReportSettingsBox firm={firm} onOpen={onOpenReportSettings} />
          )}

          <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5">
            <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Notlar</h3>
            {readOnlyMode || !hasPermission("firms.editDetails") ? (
              <InfoField label="Not" value={form.note || "-"} icon={FaStickyNote} />
            ) : (
              <EditableField
                label="Not"
                value={form.note}
                icon={FaStickyNote}
                onChange={(val) => updateForm("note", val)}
                isDirty={isDirty("note")}
                placeholder="Not ekleyin..."
                multiline
              />
            )}
          </section>
          </div>
        </div>
      </div>

      {/* Sticky Save Bar */}
      {hasChanges && !readOnlyMode && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 sm:px-5 sm:py-3">
          <div className="flex items-center justify-between">
            <p className="text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Kaydedilmemiş değişiklikler var</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={handleReset} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50">
                <FaUndo className="w-3 h-3" /> <span>Geri Al</span>
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl hover:from-indigo-700 hover:to-blue-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaSave className="w-3.5 h-3.5" />}
                <span>{saving ? "Kaydediliyor..." : "Kaydet"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoTab;
